/**
 * Vertical Line — with a timestamp label on the TIME (x) axis.
 *
 * This deliberately OVERRIDES klinecharts' built-in `verticalStraightLine`
 * (`registerOverlay` overwrites the registry by name — the same technique
 * `horizontalLineOverlay.ts` / `horizontalRayOverlay.ts` document).
 *
 * The label rides the x-axis gutter (via `createXAxisFigures`) as an
 * always-visible pill — matching where the price pill sits for the horizontal
 * line, so the two tools read as a symmetric pair (time on the bottom axis,
 * price on the side axis). The date is shorthand, e.g.
 * "Thu 18 June 26" (daily) or "Thu 18 June 26 14:30" (intraday).
 *
 * Note: in "hide axis scale" mode ChartManager collapses the x-axis strip to
 * 1px, so this pill is hidden along with the axis — an accepted trade-off of
 * axis placement (the label was explicitly requested ON the axis).
 *
 * The name stays `verticalStraightLine` on purpose — it is the persisted key
 * (`SavedDrawing.name`); renaming would orphan every saved vertical line.
 */
import { registerOverlay } from 'klinecharts'
import { CHART_COLORS, CHART_FONT_FAMILY } from './chartColors'
import { inkOn } from './tpoSettings'
import { formatShorthandDate, isDailyNormalizedTimestamp } from './overlayDateFormat'
import { isNum, type OverlayFigure } from './overlayUtils'
import {
  DEFAULT_FIGURES,
  NO_DEFAULT_FIGURES,
  type OverlayTemplateParam,
  registerOnce,
} from './registrationUtils'

/** Persisted name of the primary, user-drawn vertical line (candle pane). */
export const VERTICAL_LINE_NAME = 'verticalStraightLine'

/**
 * Name of the per-sub-pane "span" segment. NOT persisted and NOT user-drawable:
 * ChartManager mirrors one of these onto every sub-pane (volume, indicator
 * studies) so the vertical line reads as ONE continuous line down the whole
 * chart rather than stopping at the bottom of the candle pane.
 *
 * Why a mirror overlay and not a taller line: klinecharts renders each pane on
 * its OWN canvas and stores overlays per-`paneId` (`OverlayStore.getInstances`
 * filters by pane), so a single overlay can only ever draw inside its own pane
 * and `bounding.height` is always that pane's height — a bigger height is
 * clipped to the pane canvas. The built-in crosshair spans by drawing one
 * segment per pane widget (`CrosshairLineView` runs on every pane); this
 * mirrors exactly that pattern for the drawn vertical line.
 */
export const VERTICAL_LINE_SPAN_NAME = 'verticalLineSpan'

/** Style the toolbar can override per drawing (lands on `overlay.styles.line`). */
export interface VerticalLineStyle {
  color?: string
  size?: number
  style?: 'solid' | 'dashed'
}

/** Concrete pill background when the toolbar has set no line colour. */
const PILL_FALLBACK_COLOR = CHART_COLORS.textMuted

export interface VerticalLineFigureInput {
  /** Pixel x of the line (from the overlay's single point). */
  x: number
  /** Pane height in pixels — the line spans it top to bottom. */
  height: number
  /** The toolbar's line colour, or undefined (line then inherits the theme). */
  color?: string
  /** Optional line width / dash from the toolbar. */
  size?: number
  lineStyle?: 'solid' | 'dashed'
}

/**
 * Pure builder for the vertical line itself (no label — the label rides the
 * x-axis, see `buildVerticalLineAxisFigures`). Bails to `[]` on non-finite
 * geometry (klinecharts would render garbage otherwise).
 */
export function buildVerticalLineFigures(input: VerticalLineFigureInput): OverlayFigure[] {
  const { x, height, color, size, lineStyle } = input
  if (!isNum(x) || !isNum(height)) return []

  return [
    {
      key: 'vertical_line',
      type: 'line',
      attrs: {
        coordinates: [
          { x, y: 0 },
          { x, y: height },
        ],
      },
      styles: {
        // Conditional, like horizontalRayOverlay: an unstyled line keeps the
        // theme overlay colour rather than being forced to a concrete value.
        ...(color ? { color } : {}),
        ...(size ? { size } : {}),
        ...(lineStyle === 'dashed' ? { style: 'dashed', dashedValue: [4, 4] } : {}),
      },
    },
  ]
}

/**
 * Pure builder for the x-axis (time) pill, centred on the line's x and drawn in
 * the x-axis gutter. `''` label draws nothing. Text colour is DERIVED from the
 * pill background (inkOn) so a light line colour (e.g. the default yellow
 * #eab308) never renders as illegible white-on-yellow.
 */
export function buildVerticalLineAxisFigures(
  x: number,
  axisHeight: number,
  label: string,
  color?: string,
): OverlayFigure[] {
  if (!isNum(x) || !isNum(axisHeight) || !label) return []
  const pillBg = color ?? PILL_FALLBACK_COLOR
  return [
    {
      type: 'text',
      ignoreEvent: true,
      attrs: { x, y: axisHeight / 2, text: label, align: 'center', baseline: 'middle' },
      styles: {
        style: 'fill',
        color: inkOn(pillBg),
        size: 11,
        // Explicit family: 'inherit' is invalid on a canvas and klinecharts
        // mis-measures the label box otherwise (see referencePriceLine).
        family: CHART_FONT_FAMILY,
        weight: 'normal',
        backgroundColor: pillBg,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 2,
        paddingBottom: 2,
        borderRadius: 2,
      },
    },
  ]
}

/** Register the timestamped vertical line. Idempotent. */
export const registerVerticalLineOverlay = registerOnce(() => {
  registerOverlay({
    name: 'verticalStraightLine',
    totalStep: 2, // single click — one point fully defines the line
    ...DEFAULT_FIGURES,
    // The default x-axis time pill (selected-only) is replaced by the
    // always-visible custom x-axis pill below.
    needDefaultXAxisFigure: false,
    createPointFigures: ({
      coordinates,
      bounding,
      overlay,
    }: {
      coordinates: Array<{ x: number; y: number }>
      bounding: { height: number }
      overlay: { styles?: { line?: VerticalLineStyle } }
    }) => {
      const c = coordinates[0]
      if (!c) return []
      const line = overlay.styles?.line ?? {}
      return buildVerticalLineFigures({
        x: c.x,
        height: bounding.height,
        color: line.color,
        size: line.size,
        lineStyle: line.style === 'dashed' ? 'dashed' : 'solid',
      })
    },
    createXAxisFigures: ({
      coordinates,
      bounding,
      overlay,
      dateTimeFormat,
    }: {
      coordinates: Array<{ x: number; y: number }>
      bounding: { height: number }
      overlay: { points: Array<{ timestamp?: number }>; styles?: { line?: VerticalLineStyle } }
      dateTimeFormat: Intl.DateTimeFormat
    }) => {
      const c = coordinates[0]
      const ts = overlay.points?.[0]?.timestamp
      if (!c || !isNum(ts)) return []
      // Daily+ bars are noon-UTC-normalized by the backend, so drop the time
      // suffix for them ("Thu 18 June 26"); intraday keeps it ("… 14:30"). The
      // chart's formatter always carries an hour field (the crosshair shows
      // time even on daily), so it can't be used to detect granularity — the
      // noon-UTC check can. `timeZone` still comes from it so the pill date
      // matches the crosshair.
      const opts = dateTimeFormat.resolvedOptions()
      const label = formatShorthandDate(ts, opts.timeZone, !isDailyNormalizedTimestamp(ts))
      return buildVerticalLineAxisFigures(c.x, bounding.height, label, overlay.styles?.line?.color)
    },
  } as OverlayTemplateParam)
})

/**
 * Register the sub-pane span segment. Idempotent.
 *
 * Line-only and inert: no default point handles, no axis pills (the primary on
 * the candle pane owns the x-axis time pill — a pill per sub-pane would stamp
 * the same label repeatedly), and the figure sets `ignoreEvent` so hit-testing,
 * hover, and selection all belong to the primary. ChartManager keeps each span
 * segment's `x` (timestamp) and line style in lockstep with its primary; the
 * FULL-HEIGHT geometry is automatic because each pane calls this with its own
 * `bounding.height`.
 */
export const registerVerticalLineSpanOverlay = registerOnce(() => {
  registerOverlay({
    name: VERTICAL_LINE_SPAN_NAME,
    totalStep: 2,
    ...NO_DEFAULT_FIGURES,
    createPointFigures: ({
      coordinates,
      bounding,
      overlay,
    }: {
      coordinates: Array<{ x: number; y: number }>
      bounding: { height: number }
      overlay: { styles?: { line?: VerticalLineStyle } }
    }) => {
      const c = coordinates[0]
      if (!c) return []
      const line = overlay.styles?.line ?? {}
      return buildVerticalLineFigures({
        x: c.x,
        height: bounding.height,
        color: line.color,
        size: line.size,
        lineStyle: line.style === 'dashed' ? 'dashed' : 'solid',
      }).map((f) => ({ ...f, key: 'vertical_line_span', ignoreEvent: true }))
    },
  } as OverlayTemplateParam)
})
